import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, PageHeaderSkeleton, type Crumb } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { EditableDataGrid } from "@/components/editable-data-grid";
import { SpecPilotSourcePdfPanel } from "@/components/spec-pilot-source-pdf-panel";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SubmittalSelectionBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getTableRowsPayload, type TableRowsPayload, type TableRowsPayloadRef } from "@/lib/table-rows-cache";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";
import { useCanEdit } from "@/lib/capabilities";

// ============================================================================
// Route Definition
// ============================================================================

export const Route = createFileRoute("/specPilot/$jobId_/tables/$tableId")({
  component: TableDetailPage,
});

// ============================================================================
// Page Component
// ============================================================================

const SPEC_PILOT_CRUMB: Crumb = { label: "SpecPilot", href: "/specPilot" };

function MetadataItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function TableDetailPage() {
  const { jobId, tableId } = Route.useParams();
  const { data: job } = useQuery({
    ...convexQuery(api.specPilot.submittals.get, {
      submittalId: jobId as Id<"specPilotSubmittals">,
    }),
    throwOnError: true,
  });

  return (
    <ActionLogProvider
      projectId={job?.projectId ?? null}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={jobId}
    >
      <TableDetailContent job={job} jobId={jobId} tableId={tableId} />
    </ActionLogProvider>
  );
}

function TableDetailContent({
  job,
  jobId,
  tableId,
}: {
  job?: Doc<"specPilotSubmittals"> | null;
  jobId: string;
  tableId: string;
}) {
  const canEdit = useCanEdit();

  // Fetch table and job data
  const { data: table } = useQuery(convexQuery(api.specPilot.extractedTables.get, {
    tableId: tableId as Id<"specPilotExtractedTables">,
  }));
  const { data: rowPayloadRef } = useQuery(
    convexQuery(
      api.specPilot.extractedTables.getRowsPayloadRef,
      {
        tableId: tableId as Id<"specPilotExtractedTables">,
      }
    )
  );
  const recordActionLog = useRecordActionLog();
  const { data: sourcePdf } = useQuery(
    convexQuery(
      api.specPilot.submittals.getSourcePdfUrl,
      job ? { submittalId: jobId as Id<"specPilotSubmittals"> } : "skip"
    )
  );
  const [rowPayload, setRowPayload] = useState<TableRowsPayload | null>(null);
  const [rowPayloadError, setRowPayloadError] = useState<string | null>(null);
  const [isHydratingRows, setIsHydratingRows] = useState(false);

  // Mutation for updating table data
  const updateTableData = useMutation(api.specPilot.extractedTables.updateData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);

  // Handle save
  const handleSave = async (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[] }
  ) => {
    try {
      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/rows",
            filename: `${tableId}-rows.json.gz`,
          }),
        headers,
        rows,
      });

      await updateTableData({
        tableId: tableId as Id<"specPilotExtractedTables">,
        headers,
        rowsStorageKey: uploadedRows.rowsStorageKey,
        rowsFormat: uploadedRows.rowsFormat,
        rowCount: uploadedRows.rowCount,
        deletedRowIndices: metadata?.deletedRowIndices ?? [],
      });
      await recordActionLog();
      toast.success("Table saved successfully");
    } catch (error) {
      notifyError(error, "Failed to save table");
      console.error("Error saving table:", error);
      throw error; // Re-throw to let EditableDataGrid handle the error state
    }
  };

  useEffect(() => {
    let cancelled = false;
    setRowPayload(null);

    if (rowPayloadRef === undefined) return () => {
      cancelled = true;
    };

    if (rowPayloadRef === null) {
      setRowPayload(null);
      setRowPayloadError("Table rows could not be loaded.");
      return () => {
        cancelled = true;
      };
    }

    async function hydrateRows() {
      setIsHydratingRows(true);
      setRowPayloadError(null);
      try {
        const payload = await getTableRowsPayload(
          rowPayloadRef as TableRowsPayloadRef
        );
        if (!cancelled) {
          setRowPayload(payload);
        }
      } catch (error) {
        console.error("Failed to load table rows", error);
        if (!cancelled) {
          setRowPayload(null);
          setRowPayloadError("Failed to load table rows.");
        }
      } finally {
        if (!cancelled) {
          setIsHydratingRows(false);
        }
      }
    }

    void hydrateRows();
    return () => {
      cancelled = true;
    };
  }, [rowPayloadRef]);

  const hydratedTable = useMemo(() => {
    if (!table || !rowPayload) return null;
    return {
      ...table,
      headers: rowPayload.headers,
      rows: rowPayload.rows,
    };
  }, [table, rowPayload]);

  // Loading state
  if (
    table === undefined ||
    job === undefined ||
    rowPayloadRef === undefined ||
    (table !== null && !rowPayload && !rowPayloadError && isHydratingRows)
  ) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <Card>
          <ProcessingSpinner message="Loading table data..." />
        </Card>
      </div>
    );
  }

  // Table not found state
  if (table === null) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Table Not Found"
          trail={[
            SPEC_PILOT_CRUMB,
            { label: job?.name ?? "Job", href: "/specPilot/$jobId", params: { jobId } },
          ]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Table not found</AlertTitle>
          <AlertDescription>
            The table you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Job not found state (table exists but job doesn't - orphaned data)
  if (job === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Job Not Found" trail={[SPEC_PILOT_CRUMB]} />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job associated with this table does not exist or has been
            deleted.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const tableName = table.name || "Untitled Table";
  const rowCount = hydratedTable?.rows.length ?? table.rowCount ?? 0;
  const colCount = hydratedTable?.headers.length ?? table.headerCount ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={tableName}
        trail={[
          SPEC_PILOT_CRUMB,
          { label: job.name, href: "/specPilot/$jobId", params: { jobId } },
        ]}
        meta={
          <SubmittalSelectionBadge selected={table.selectedForSubmittal} />
        }
      />

      {/* Table metadata — the name is the page title, so it is not repeated here. */}
      <Card className="py-3">
        <CardContent className="px-4">
          <dl className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <MetadataItem label="Page">{table.pageNumber}</MetadataItem>
            <MetadataItem label="Dimensions">
              {rowCount} rows x {colCount} columns
            </MetadataItem>
            {table.extractionMethod && (
              <MetadataItem label="Extraction Method">
                <Badge variant="secondary" size="sm">
                  {table.extractionMethod === "pdfplumber"
                    ? "PDF Plumber"
                    : "Vision AI"}
                </Badge>
              </MetadataItem>
            )}
          </dl>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,42%)]">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="text-base">Table Data</CardTitle>
            {canEdit && (
              <CardDescription>
                Click any cell to edit. Changes are saved when you click the Save
                button.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {rowPayloadError ? (
              <Alert variant="destructive">
                <AlertCircle className="size-4" />
                <AlertTitle>Unable to load rows</AlertTitle>
                <AlertDescription>{rowPayloadError}</AlertDescription>
              </Alert>
            ) : hydratedTable ? (
              <EditableDataGrid
                headers={hydratedTable.headers}
                rows={hydratedTable.rows}
                readOnly={!canEdit}
                onSave={handleSave}
              />
            ) : (
              <ProcessingSpinner message="Loading table rows..." />
            )}
          </CardContent>
        </Card>

        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Source PDF</CardTitle>
            <CardDescription>
              Showing page {table.pageNumber} for the active table.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SpecPilotSourcePdfPanel
              sourcePdf={sourcePdf}
              sourceId={jobId}
              pageNumber={table.pageNumber}
              // ~280px = TopNav + sticky 48px PageHeader + this card's header/padding + page
              // bottom padding, so the panel fits the viewport once the grid row is scrolled to.
              className="h-[70dvh] min-h-[480px] lg:h-[calc(100dvh-280px)] lg:min-h-[560px]"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/*
 * Usage Example:
 *
 * The table detail page is automatically rendered when navigating to
 * /specPilot/:jobId/tables/:tableId. It provides inline editing capabilities
 * for the extracted table data.
 *
 * Example navigation:
 * <Link to="/specPilot/$jobId/tables/$tableId" params={{ jobId: "123", tableId: "456" }}>
 *   View Table
 * </Link>
 *
 * Accessibility Checklist:
 * - Inline header trail (nav aria-label="Breadcrumb") back to SpecPilot and the job
 * - All interactive elements are keyboard accessible
 * - Status changes announced via toast notifications
 * - Proper heading hierarchy maintained
 * - Color contrast meets WCAG requirements
 *
 * Performance Considerations:
 * - Uses Convex real-time subscriptions for automatic updates
 * - EditableDataGrid manages local state to minimize re-renders
 * - Lazy loading of table data on route entry
 * - Save operations are batched (not per-keystroke)
 */
